import { useMemo } from "react";
import { trpc } from "../../../lib/api/trpc";
import type { Document } from "../../../types/documents";

const PROCESSING_STATUSES = ["PRESIGNED", "QUEUED", "PROCESSING", "PROCESSED"];
const POLL_INTERVAL_MS = 7000; // 7 seconds

/**
 * Hook to fetch all documents for the current organization
 * Automatically polls when documents are in processing states
 * Optionally filters by collectionId and archived status
 */
export const useDocuments = (
  collectionId?: string,
  includeArchived?: boolean,
) => {
  const input =
    collectionId || includeArchived
      ? { collectionId, includeArchived }
      : undefined;

  // @ts-ignore - Type checking for trpc.documents property
  const { data, isLoading, isError, error, refetch } =
    trpc.documents.list.useQuery(input, {
      // Enable refetch interval only when there are processing documents
      refetchInterval: (data: unknown) => {
        if (!data) return false;

        const hasProcessingDocuments = (data as Document[]).some((doc) =>
          PROCESSING_STATUSES.includes(doc.status),
        );

        return hasProcessingDocuments ? POLL_INTERVAL_MS : false;
      },
    });

  // Compute whether any documents are currently processing
  const hasProcessingDocuments = useMemo(() => {
    if (!data) return false;
    return (data as Document[]).some((doc) =>
      PROCESSING_STATUSES.includes(doc.status),
    );
  }, [data]);

  return {
    documents: (data as Document[]) || [],
    isLoading,
    isError,
    error,
    refetch,
    hasProcessingDocuments,
  };
};
